import { EFinancingProgram, EOriginSystemId } from '@if/core/types'
import { CalculatorEstimateRequestDto, CalculatorEstimateResultDto } from '@src/types/CalculatorEstimateDto'
import { ComputeMechanicalWarrantyRequestDto } from '@src/types/ComputeMechanicalWarrantyRequestDto'
import { ComputeMechanicalWarrantyResultDto } from '@src/types/ComputeMechanicalWarrantyResultDto'
import { ProductsComputeRequestDto } from '@src/types/ProductsComputeRequestDto'
import { ProductsComputeResultDto } from '@src/types/ProductsComputeResultDto'
import { keepPreviousData, QueryFunctionContext, useQuery } from '@tanstack/react-query'
import { getApiClient } from '../api-client'

const SCOPE = 'calculator-rate'
const CALCULATE = 'calculate'

const keysFactory = {
  computeProductsLoan: (calculator: ProductsComputeRequestDto | undefined) =>
    [{ scope: SCOPE, entity: CALCULATE, calculator }] as const,
  computeMechanicalWarrantyLoan: (calculator: ComputeMechanicalWarrantyRequestDto | undefined) =>
    [{ scope: SCOPE, entity: CALCULATE, calculator }] as const,
  estimate: (financingProgramId: EFinancingProgram, request: CalculatorEstimateRequestDto | null) =>
    [{ scope: SCOPE, entity: 'estimate', financingProgramId, ...request }] as const,
  firstPaymentOptions: (financingProgramId: EFinancingProgram, deliveryOn: string, paymentFrequency: string) =>
    [{ scope: SCOPE, entity: 'firstPaymentOptions', financingProgramId, deliveryOn, paymentFrequency }] as const,
}

const firstPaymentOptions = async (
  financingProgramId: EFinancingProgram,
  deliveryOn: string,
  paymentFrequency: string,
): Promise<string[]> => {
  const response = await getApiClient().post<string[]>(`/Calculator/${financingProgramId}/FirstPaymentOptions`, {
    deliveryOn,
    systemOriginId: EOriginSystemId.MerchantDashboard,
    isHighEstimate: false,
    paymentFrequency,
  })
  return response.data
}

/**
 * The first payment dates the program allows. A fixed offset can land on a date the program
 * would reject, which then prices differently from the worksheet.
 */
export const useFirstPaymentOptions = (
  financingProgramId: EFinancingProgram,
  deliveryOn: string,
  paymentFrequency: string,
): string[] => {
  const { data } = useQuery({
    queryKey: keysFactory.firstPaymentOptions(financingProgramId, deliveryOn, paymentFrequency),
    queryFn: () => firstPaymentOptions(financingProgramId, deliveryOn, paymentFrequency),
    enabled: Boolean(deliveryOn && paymentFrequency),
    staleTime: Infinity,
  })
  return data ?? []
}

const calculatorEstimate = async (
  financingProgramId: EFinancingProgram,
  request: CalculatorEstimateRequestDto,
): Promise<CalculatorEstimateResultDto> => {
  const response = await getApiClient().post<CalculatorEstimateResultDto>(
    `/Calculator/${financingProgramId}/Estimate`,
    request,
  )
  return response.data
}

/**
 * The estimate, priced by the credit engine. Pass `null` until the form has everything; the
 * previous result is kept while a new one is in flight so the payment card doesn't blank out.
 */
const useCalculatorEstimate = (
  financingProgramId: EFinancingProgram,
  request: CalculatorEstimateRequestDto | null,
): [CalculatorEstimateResultDto | null, boolean] => {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.estimate(financingProgramId, request),
    queryFn: () => calculatorEstimate(financingProgramId, request!),
    enabled: request !== null,
    placeholderData: keepPreviousData,
  })

  return [data ?? null, isFetching]
}

const computeProductsLoan = async (
  _ctx: QueryFunctionContext<ReturnType<(typeof keysFactory)['computeProductsLoan']>>,
  calculator: ProductsComputeRequestDto | undefined,
) => {
  const apiClient = getApiClient()

  const response = await apiClient.post<ProductsComputeResultDto>(
    `/${EFinancingProgram.Products}/Calculator/Compute`,
    calculator,
  )

  return response.data
}

const useComputeProductsLoan = (
  dto: ProductsComputeRequestDto | undefined,
): [ProductsComputeResultDto | null, boolean] => {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.computeProductsLoan(dto),
    queryFn: (ctx) => computeProductsLoan(ctx, dto),
    enabled: !!dto,
  })

  return [data ?? null, isFetching]
}

const computeMechanicalWarrantyLoan = async (
  _ctx: QueryFunctionContext<ReturnType<(typeof keysFactory)['computeMechanicalWarrantyLoan']>>,
  calculator: ComputeMechanicalWarrantyRequestDto | undefined,
) => {
  const apiClient = getApiClient()

  const response = await apiClient.post<ComputeMechanicalWarrantyResultDto>(
    `/Calculator/${EFinancingProgram.MechanicalWarranty}/Compute`,
    calculator,
  )

  return response.data
}

const useComputeMechanicalWarrantyLoan = (
  dto: ComputeMechanicalWarrantyRequestDto | undefined,
): [ComputeMechanicalWarrantyResultDto | null, boolean] => {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.computeMechanicalWarrantyLoan(dto),
    queryFn: (ctx) => computeMechanicalWarrantyLoan(ctx, dto),
    enabled: !!dto,
  })

  return [data ?? null, isFetching]
}

export { useCalculatorEstimate, useComputeMechanicalWarrantyLoan, useComputeProductsLoan }
